import { useCallback, useMemo, useState } from 'react'
import { useCookies } from 'react-cookie'
import { sessionKey, useUserSession } from 'features/session/context'
import { useTenant } from 'features/tenant/context'
import {
  AppBar,
  Toolbar,
  Stack,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Button,
  IconButton,
  Link,
  Avatar,
  Chip,
  Tooltip,
  useTheme,
  useMediaQuery,
  BreadcrumbsProps,
  Breadcrumbs,
  styled,
} from '@mui/material'
import { MenuIcon, ProfileIcon, LogoutIcon, SeparatorIcon } from 'shared/icons'
import { Home as HomeIcon } from '@mui/icons-material'

import { useGetSelfUserIcon, UserRole } from 'interface/user'

import { EditProfileDialog } from 'features/user/profile/dialog/edit'

import { BreadCrumbItem } from './types'

import { useTopBar } from './container'
import { useSideMenu } from 'components/sideMenu/container'

const StyledBreadcrumbs = styled(Breadcrumbs)(() => {
  return {
    '& .MuiBreadcrumbs-separator': {
      marginLeft: 8,
      marginRight: 8
    },
    '& .MuiBreadcrumbs-ol': {
      overflow: 'hidden'
    },
    '& .MuiBreadcrumbs-li': {
      overflow: 'hidden',
      position: 'relative'
    }
  }
})

type TopPageBreadCrumbProps = {
  crumbs: BreadCrumbItem[]
} & BreadcrumbsProps
const TopPageBreadCrumbs = ({
  crumbs,
  ...others
}: TopPageBreadCrumbProps) => {
  return (
    <StyledBreadcrumbs
      separator={<SeparatorIcon fontSize='small'/>}
      maxItems={5}
      style={{
        overflow: 'hidden'
      }}
      {...others}
    >
      {crumbs.map((item, i) => {
        const style = {
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: '100%'
        }
        return item.url ? (
          <Link
            underline="hover"
            color="inherit"
            key={`item-${i}`}
            variant="body1"
            href={item.url}
            sx={style}
          >
            {item.label}
          </Link>
        ) : (
          <Typography
            color="text.primary"
            key={`item-${i}`}
            variant="h6"
            component="div"
            sx={style}
          >
            {item.label}
          </Typography>
        )
      })}
    </StyledBreadcrumbs>
  )
}

type TopBarProps = {
  topBarState: ReturnType<typeof useTopBar>
  sideMenuState: ReturnType<typeof useSideMenu>
	onLogoff: (keepUrl: boolean) => void
}
export const TopBar = ({
  topBarState: {
    breadCrumbs
  },
  sideMenuState: {
    setSideBarOpen
  },
  onLogoff
}: TopBarProps) => {
  const theme = useTheme()
  const isSmallMedia = useMediaQuery(theme.breakpoints.down('sm'))

  const [, setCookie, removeCookie] = useCookies([sessionKey])

  const userSession = useUserSession()
  const { userName, groupName, inside, hasRole, isDemo } = userSession

  const tenant = useTenant()

  const [modal, setModal] = useState<'profile' | null>(null)
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<null | HTMLElement>(null)

  const onClickOpenUserProfile = useCallback(
    () => {
      setUserMenuAnchorEl(null)
      setModal('profile')
    },
    []
  )
  const userIconState = useGetSelfUserIcon()
  const userIcon = useMemo(
    () => userIconState.data ? URL.createObjectURL(userIconState.data.blob) : '',
    [userIconState]
  )
  return (
    <AppBar
      position="static"
      style={isSmallMedia ? {
        borderBottom: '3px solid #ad0026',
        flexDirection: 'row',
        background: '#fff',
        color: '#4b4948',
        flexWrap: 'wrap-reverse',
        justifyContent: 'flex-end'
      } : {
        borderBottom: '3px solid #ad0026',
        flexDirection: 'row',
        background: '#fff',
        color: '#4b4948'
      }}
    >
      <Toolbar
        variant={isSmallMedia ? "dense" : undefined}
        sx={{
          flexGrow: 1,
          overflow: 'hidden',
          paddingX: 1
        }}
      >
        <IconButton
          edge="start"
          size="small"
          color="inherit"
          aria-label="menu"
          onClick={() => setSideBarOpen(true)}
          sx={{ mr: 1 }}
        >
          <MenuIcon />
        </IconButton>

        <IconButton
          edge="start"
          size="small"
          color="inherit"
          aria-label="home"
          href={`/${tenant.domain}/search/list`}
          sx={{ mr: 1 }}
        >
          <HomeIcon />
        </IconButton>

        <TopPageBreadCrumbs
          crumbs={breadCrumbs}
          sx={{ flexGrow: 1 }}
        />
      </Toolbar>

      <Toolbar
        variant={isSmallMedia ? "dense" : undefined}
        sx={{
          paddingX: 0,
          minHeight: isSmallMedia ? 32 : undefined
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          sx={{ textTransform: 'none' }}
        >
          {hasRole(UserRole.CareNet) && (
            inside ? (
              <Tooltip title="CareNetデータへのアクセスができます">
                <Chip label="CareNet" color="careNet" size="small" sx={{ mr: 1 }}/>
              </Tooltip>
            ) : (
              <Tooltip title="IP制限によりCareNetデータへのアクセスができません">
                <Chip label="CareNet" color="default" size="small" sx={{ mr: 1 }}/>
              </Tooltip>
            )
          )}
          {isDemo && (
            <Tooltip title="施設/医師情報は匿名化されています">
              <Chip label="匿名化" color="secondary" size="small" sx={{ mr: 1 }}/>
            </Tooltip>
          )}
        </Stack>

        <Button
          color="inherit"
          size={isSmallMedia ? "small" : undefined}
          startIcon={
            <Avatar
              src={userIcon}
              alt={userName}
              sx={isSmallMedia ? {
                height: '1.5rem',
                width: '1.5rem'
              } : {
                height: '2rem',
                width: '2rem'
              }}
            />
          }
          onClick={(event) => setUserMenuAnchorEl(event.currentTarget)}
          sx={{
            height: "100%",
            paddingX: 2
          }}
        >
          <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" sx={{ textTransform: 'none' }}>
            <Typography color="text.primary" variant="body2" whiteSpace="nowrap" fontWeight={500} component="div">{userName}</Typography>
            <Typography color="text.secondary" variant="body2" whiteSpace="nowrap" component="div">/ {groupName}</Typography>
          </Stack>
        </Button>

        <Menu
          id="topbar-usersmenu"
          anchorEl={userMenuAnchorEl}
          keepMounted
          open={Boolean(userMenuAnchorEl)}
          onClose={() => setUserMenuAnchorEl(null)}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
          }}
        >
          <MenuItem onClick={onClickOpenUserProfile}>
            <ListItemIcon>
              <ProfileIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>プロフィール</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => onLogoff(false)}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>ログアウト</ListItemText>
          </MenuItem>
        </Menu>

        {modal === 'profile' ? (
          <EditProfileDialog
            onChangeUserIcon={() => {
              userIconState.refetch()
            }}
            onChangeUserName={(user) => {
              removeCookie(sessionKey)
              setCookie(
                sessionKey,
                encodeURIComponent(
                  JSON.stringify({
                    ...userSession,
                    userName: user.userName,
                  })
                )
              )
            }}
            onClose={() => setModal(null)}
          />
        ) : <></> }
      </Toolbar>
    </AppBar>
  )
}
